import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LayoutDashboard, AlertTriangle, Trash2, Power, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Ce a făcut Atena, într-un singur loc — și ce n-a mers.
 *
 * Un tablou care arată numai reușite nu răspunde la întrebarea pentru care
 * există: „de ce n-a mers?". Deci fiecare secțiune poartă și ce a picat, iar
 * lipsa datelor se scrie ca lipsă, nu ca zero.
 */
const ZILE = [[1, "azi"], [7, "7 zile"], [30, "30 zile"]];

export default function TablouDeBord({ workspaceId }) {
  const [zile, setZile] = useState(1);
  const [date, setDate] = useState(null);
  const [eroare, setEroare] = useState("");
  const [busy, setBusy] = useState(false);

  const incarca = useCallback(async () => {
    try {
      const { data } = await api.get("/tablou", {
        params: { workspace_id: workspaceId, zile }, timeout: 120000,
      });
      setDate(data);
      setEroare("");
    } catch (e) {
      setDate(null);
      setEroare(e?.response?.data?.detail || "Nu am putut citi tabloul");
    }
  }, [workspaceId, zile]);

  useEffect(() => { if (workspaceId) incarca(); }, [workspaceId, incarca]);

  const opresteRutina = async (r) => {
    setBusy(true);
    try {
      await api.patch(`/rutine/${r.id}`, {
        workspace_id: workspaceId, nume: r.nume, frecventa: r.frecventa,
        ora: Number(r.ora.split(":")[0]), minut: Number(r.ora.split(":")[1]),
        prompt: r.prompt, activa: !r.activa,
      });
      toast.success(r.activa ? "Rutina a fost oprită" : "Rutina a fost pornită");
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut schimba rutina");
    } finally { setBusy(false); }
  };

  const stergePreferinta = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/invatare/preferinte/${id}`, { params: { workspace_id: workspaceId } });
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut șterge");
    } finally { setBusy(false); }
  };

  if (!date && !eroare) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="tablou">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <LayoutDashboard className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Ce a făcut Atena</h3>
        <div className="ml-auto flex items-center gap-1">
          {ZILE.map(([z, eticheta]) => (
            <button key={z} type="button" data-testid={`tablou-zile-${z}`}
                    onClick={() => setZile(z)}
                    className={`px-2 py-1 text-[11px] border transition-colors ${
                      zile === z ? "border-brand/50 text-brand bg-brand/10"
                                 : "border-line/20 text-fg/50 hover:text-fg"}`}>
              {eticheta}
            </button>
          ))}
          <button type="button" data-testid="tablou-reincarca" onClick={incarca}
                  aria-label="Reîncarcă" className="p-1 text-fg/40 hover:text-fg">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5 text-sm">
        {eroare && <p className="text-xs text-red-400" data-testid="tablou-eroare">{eroare}</p>}

        {date && (
          <>
            {date.conturi_picate.length > 0 && (
              <div className="border border-amber-400/30 bg-amber-400/5 p-3"
                   data-testid="tablou-conturi-picate">
                <p className="text-xs text-amber-400 flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {date.conturi_picate.length} cont(uri) nu răspund
                </p>
                {date.conturi_picate.map((c) => (
                  <p key={c.id} className="text-xs text-fg/60">
                    <span className="mono">{c.nume}</span> — {c.motiv}
                  </p>
                ))}
              </div>
            )}

            <div data-testid="tablou-unelte">
              <p className="label-tiny mb-2">ce a folosit</p>
              {date.unelte.length === 0 ? (
                <p className="text-xs text-fg/40">Nicio unealtă în intervalul ăsta.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {date.unelte.map((u) => (
                    <span key={u.unealta}
                          className="px-2 py-0.5 text-[11px] mono border border-line/20 text-fg/60">
                      {u.unealta} · {u.de_cate_ori}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="tablou-modele">
              <p className="label-tiny mb-2">ce merge local, ce urcă</p>
              <ul className="space-y-1">
                {date.modele.map((m) => (
                  <li key={m.sarcina} className="text-xs flex items-center gap-2">
                    <span className={m.local ? "text-brand" : "text-fg/40"}>
                      {m.local ? "local" : "sus"}
                    </span>
                    <span className="text-fg/70">{m.eticheta}</span>
                    <span className="mono text-fg/30">{m.model}</span>
                    <span className="text-fg/30">— {m.motiv}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="tablou-cost">
              <p className="label-tiny mb-2">cost estimat</p>
              <p className="text-xs">
                <span className="font-bold">${date.cost.total}</span>{" "}
                <span className="text-fg/40">din {date.cost.apeluri} apeluri</span>
                {date.cost.fara_pret > 0 && (
                  <span className="text-amber-400">
                    {" "}· {date.cost.fara_pret} fără preț cunoscut
                  </span>
                )}
              </p>
              {date.cost.pe_model.map((m) => (
                <p key={m.model} className="text-[11px] text-fg/50">
                  <span className="mono">{m.model}</span> — ${m.cost}
                  {" "}({m.apeluri} apeluri, {m.jetoane_intrare + m.jetoane_iesire} jetoane)
                  {m.cost_necunoscut && <span className="text-amber-400"> · preț necunoscut</span>}
                </p>
              ))}
              <p className="text-[11px] text-fg/30 mt-1">{date.cost.nota}</p>
            </div>

            <div data-testid="tablou-rutine">
              <p className="label-tiny mb-2">rutine</p>
              {date.rutine.length === 0 ? (
                <p className="text-xs text-fg/40">Nicio rutină.</p>
              ) : (
                <ul className="space-y-1">
                  {date.rutine.map((r) => (
                    <li key={r.id} className="text-xs flex items-center gap-2">
                      <button type="button" onClick={() => opresteRutina(r)} disabled={busy}
                              data-testid={`tablou-rutina-comuta-${r.id}`}
                              aria-label={`${r.activa ? "Oprește" : "Pornește"} ${r.nume}`}
                              className={r.activa ? "text-brand" : "text-fg/30"}>
                        <Power className="w-3.5 h-3.5" />
                      </button>
                      <span className={r.activa ? "text-fg/70" : "text-fg/30"}>{r.nume}</span>
                      <span className="text-fg/30">{r.eticheta_frecventa} la {r.ora}</span>
                      <span className="text-fg/30">— {r.acum}</span>
                    </li>
                  ))}
                </ul>
              )}
              {date.documente.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {date.documente.slice(0, 6).map((d) => (
                    <li key={d.id} className="text-[11px] text-fg/40 mono">{d.cale}</li>
                  ))}
                </ul>
              )}
            </div>

            <div data-testid="tablou-invatat">
              <p className="label-tiny mb-2">ce a învățat despre mine</p>
              {date.invatat.preferinte.length === 0 ? (
                <p className="text-xs text-fg/40">Nimic încă.</p>
              ) : (
                <ul className="space-y-1">
                  {date.invatat.preferinte.map((p) => (
                    <li key={p.id} className="text-xs flex items-center gap-2">
                      <span className="label-tiny w-16">{p.subiect}</span>
                      <span className="text-fg/70">{p.regula}</span>
                      <button type="button" onClick={() => stergePreferinta(p.id)}
                              disabled={busy} data-testid={`tablou-sterge-pref-${p.id}`}
                              aria-label={`Șterge ${p.regula}`}
                              className="ml-auto text-fg/30 hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {date.invatat.ultima_evaluare && (
                <p className="text-[11px] text-fg/50 mt-1">
                  {date.invatat.ultima_evaluare.propozitie}
                </p>
              )}
            </div>

            {date.email_mod_test && (
              <p className="text-[11px] text-amber-400" data-testid="tablou-email-test">
                Emailurile nu pleacă: modul de test e pornit.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
